import { useEffect, useRef, useState } from 'react';
import { IconButton, Snackbar } from '@mui/material';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import Board from './Board.jsx';
import LudoActionBar, { getFirstName } from './LudoActionBar.jsx';
import { useLudoPvP } from './useLudoPvP.js';
import LudoLeaveConfirmDialog from './LudoLeaveConfirmDialog.jsx';
import { unlockLudoAudio } from './ludoSounds.js';
import { useTokenMoveAnimation } from './useTokenMoveAnimation.js';
import './Ludo.css';
import './LudoPvPScreen.css';

async function refreshWallet(onWalletUpdate) {
  if (!onWalletUpdate) return null;
  try {
    return await onWalletUpdate();
  } catch {
    return null;
  }
}

export default function LudoPvPScreen({ user, roomId, onBack, onWalletUpdate }) {
  const {
    room,
    tokens,
    myColor,
    currentColor,
    opponentColor,
    dice,
    isRolling,
    movableTokenIds,
    lastEvent,
    mustMove,
    diceDisabled,
    isMyTurn,
    secondsLeft,
    rollSecondsLeft,
    opponentRollSecondsLeft,
    rollGraceSecondsLeft,
    opponentRollGraceSecondsLeft,
    phase,
    myTimeoutCount,
    opponentTimeoutCount,
    maxMoveTimeouts,
    ended,
    rollDice,
    moveToken,
    leaveGame,
    stake,
    opponent,
    isBotGame,
    platformCommissionPercent,
    winnerPayout,
  } = useLudoPvP(roomId, user?.id);

  const { displayTokens, isAnimating, animatingTokenId } = useTokenMoveAnimation(tokens, roomId);

  const [toast, setToast] = useState('');
  const [leaving, setLeaving] = useState(false);
  const [leaveConfirmOpen, setLeaveConfirmOpen] = useState(false);
  const walletRefreshedRef = useRef(false);

  useEffect(() => {
    if (!ended || walletRefreshedRef.current) return;
    walletRefreshedRef.current = true;
    refreshWallet(onWalletUpdate);
  }, [ended, onWalletUpdate]);

  useEffect(() => {
    unlockLudoAudio();
  }, []);

  const handleBackToLobby = async () => {
    await refreshWallet(onWalletUpdate);
    onBack();
  };

  const handleLeave = () => {
    if (ended || ['FINISHED', 'FORFEIT', 'CANCELLED'].includes(room?.status)) {
      handleBackToLobby();
      return;
    }
    setLeaveConfirmOpen(true);
  };

  const confirmLeave = async () => {
    setLeaving(true);
    try {
      await leaveGame();
      await refreshWallet(onWalletUpdate);
      setLeaveConfirmOpen(false);
      onBack();
    } catch (err) {
      setToast(err.message || 'Could not leave game');
      setLeaveConfirmOpen(false);
      onBack();
    } finally {
      setLeaving(false);
    }
  };

  if (!room) {
    return (
      <div className="ludo-pvp-screen">
        <div className="ludo-pvp-screen__loading">Loading game…</div>
      </div>
    );
  }

  const iWon = ended?.winnerUserId === user?.id;
  const botWon = isBotGame && ended && !iWon;
  const myFirstName = getFirstName(user?.name);
  const opponentFirstName = getFirstName(opponent?.name);
  const showTurnArrow = isMyTurn && room.board?.phase === 'ROLL' && !isRolling && !mustMove;
  const moveHint = isMyTurn && mustMove ? 'Move a token' : null;
  const diceColor = currentColor || myColor || 'blue';

  return (
    <div className="ludo-pvp-screen">
      <div className="ludo-screen__topbar ludo-pvp-topbar">
        <IconButton size="small" onClick={handleLeave} disabled={leaving} aria-label="Leave game">
          <ArrowBackIosNewRoundedIcon fontSize="small" />
        </IconButton>
        <span className="ludo-pvp-stake">
          {isBotGame
            ? 'Free practice vs Bot'
            : `₹${stake} · Win ₹${winnerPayout.toLocaleString('en-IN')} · ${platformCommissionPercent}% fee`}
        </span>
      </div>

      <div className="ludo-pvp-playfield">
        <div className="ludo-pvp-playfield__stack">
          <div className="ludo-board-wrap ludo-board-wrap--pvp">
            <Board
              tokens={displayTokens}
              movableTokenIds={isAnimating ? [] : movableTokenIds}
              onTokenClick={moveToken}
              lastEvent={lastEvent}
              animatingTokenId={animatingTokenId}
            />
          </div>

          <LudoActionBar
            myName={myFirstName}
            opponentName={opponentFirstName}
            myColor={myColor}
            opponentColor={opponentColor}
            dice={dice}
            isRolling={isRolling}
            onRoll={rollDice}
            diceDisabled={diceDisabled}
            diceColor={diceColor}
            showTurnArrow={showTurnArrow}
            moveHint={moveHint}
            secondsLeft={secondsLeft}
            rollSecondsLeft={rollSecondsLeft}
            opponentRollSecondsLeft={opponentRollSecondsLeft}
            rollGraceSecondsLeft={rollGraceSecondsLeft}
            opponentRollGraceSecondsLeft={opponentRollGraceSecondsLeft}
            phase={phase}
            myTimeoutCount={myTimeoutCount}
            opponentTimeoutCount={opponentTimeoutCount}
            maxMoveTimeouts={maxMoveTimeouts}
            isMyTurn={isMyTurn}
            mustMove={mustMove}
          />
        </div>
      </div>

      {ended && (
        <div className="ludo-win-overlay">
          <div className="ludo-win-card">
            <div className="ludo-win-trophy">{iWon ? '🏆' : '😔'}</div>
            <h2>{iWon ? 'You won!' : 'You lost'}</h2>
            <p>
              {isBotGame
                ? iWon
                  ? 'Nice — you beat the bot!'
                  : botWon
                    ? 'The bot won this round. Try again!'
                    : 'Practice game over.'
                : iWon
                ? ended.reason === 'FORFEIT'
                  ? ended.forfeitType === 'ROLL_TIMEOUT'
                    ? `Opponent did not roll in time. ₹${(ended.winnerPayout ?? 0).toLocaleString('en-IN')} added to your wallet.`
                    : `Opponent ran out of time. ₹${(ended.winnerPayout ?? 0).toLocaleString('en-IN')} added to your wallet.`
                  : `₹${(ended.winnerPayout ?? 0).toLocaleString('en-IN')} added to your wallet.`
                : ended.reason === 'FORFEIT'
                ? ended.forfeitType === 'ROLL_TIMEOUT'
                  ? 'You lost — dice roll timer expired.'
                  : 'You lost — timed out 3 times or left the game.'
                : 'Better luck next time.'}
            </p>
            <div className="ludo-win-actions">
              <button type="button" className="ludo-win-btn-primary" onClick={handleBackToLobby}>
                Back to lobby
              </button>
            </div>
          </div>
        </div>
      )}

      <LudoLeaveConfirmDialog
        open={leaveConfirmOpen}
        isBotGame={isBotGame}
        stake={stake}
        leaving={leaving}
        onCancel={() => setLeaveConfirmOpen(false)}
        onConfirm={confirmLeave}
      />

      <Snackbar open={Boolean(toast)} message={toast} autoHideDuration={3000} onClose={() => setToast('')} />
    </div>
  );
}
